import React, { useState } from "react";
import {
  Stack,
  TextField,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { customColors } from "models/customColors";
import { ReactComponent as SettingsIcon } from "images/table-config-icon.svg";
import { DialogWindow } from "./DialogWindow";
import RefreshIcon from "@mui/icons-material/Refresh";

/** @type {React.Dispatch<React.SetStateAction<any[]>> | undefined} */
const noop = undefined;

export const DataGridSearch = ({
  search,
  handleChangeSearch,
  clearSearch,
  columns = [],
  visibility = [],
  setVisibility = noop,
  afterSearchContent = null,
  loadData = () => null,
  withRefreshButton = false,
}) => {
  const [isColumnSelectorVisible, setIsColumnSelectorVisible] = useState(false);

  const isAllColumnsVisible = visibility.length === 0;

  const handleSelectAllClick = () => {
    if (visibility.length === columns.length) {
      setVisibility([]);
      return;
    }

    setVisibility(columns.map(({ field }) => field));
  };

  const handleSelectColumnClick = (field) => {
    if (visibility.includes(field)) {
      const newVisisbility = visibility.filter((value) => value !== field);
      setVisibility(newVisisbility);
      return;
    }

    setVisibility([...visibility, field]);
  };

  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      gap={1}
      mb={"20px"}
    >
      {withRefreshButton && (
        <IconButton onClick={loadData}>
          <RefreshIcon />
        </IconButton>
      )}
      <TextField
        value={search}
        onChange={handleChangeSearch}
        placeholder="Search"
        InputProps={{
          inputProps: {
            autoComplete: "off",
          },
          startAdornment: (
            <SearchIcon
              sx={{ color: customColors.grayText, margin: "0 10px" }}
            />
          ),
          endAdornment: !search.length ? null : (
            <ClearIcon
              onClick={clearSearch}
              sx={{ cursor: "pointer", margin: "0 10px" }}
            />
          ),
          sx: {
            height: "40px",
            borderRadius: "5px",
            backgroundColor: search === "" ? undefined : customColors.search,
          },
        }}
        sx={{
          flex: 1,
          height: "40px",
          borderRadius: "5px",
        }}
        variant="standard"
      />
      <div className="hidden md:block">
        {setVisibility && (
          <>
            <IconButton onClick={() => setIsColumnSelectorVisible(true)}>
              <SettingsIcon />
            </IconButton>
            <DialogWindow
              open={isColumnSelectorVisible}
              title={"Display Options"}
              content={
                <Stack ml={3} mt={1} gap={2}>
                  Choose what to display in the table:
                  <Grid container columns={12}>
                    <Grid item xs={4}>
                      <Stack direction={"row"}>
                        <FormControlLabel
                          label="All Fields"
                          control={
                            <Checkbox
                              checked={isAllColumnsVisible}
                              onClick={handleSelectAllClick}
                            />
                          }
                        />
                      </Stack>
                    </Grid>
                    {columns.map(({ label, field }) => {
                      if (isAllColumnsVisible) {
                        return (
                          <Grid item xs={4} key={`${label}-${field}`}>
                            <Stack direction={"row"}>
                              <FormControlLabel
                                label={label}
                                control={
                                  <Checkbox
                                    onChange={() =>
                                      handleSelectColumnClick(field)
                                    }
                                    checked={false}
                                  />
                                }
                              />
                            </Stack>
                          </Grid>
                        );
                      }

                      return (
                        <Grid item xs={4} key={`${label}-${field}`}>
                          <Stack direction={"row"}>
                            <FormControlLabel
                              label={label}
                              control={
                                <Checkbox
                                  onChange={() =>
                                    handleSelectColumnClick(field)
                                  }
                                  checked={!!visibility.includes(field)}
                                />
                              }
                            />
                          </Stack>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Stack>
              }
              handleClose={() => setIsColumnSelectorVisible(false)}
              withCloseButton
            />
          </>
        )}
      </div>
      {afterSearchContent}
    </Stack>
  );
};
